import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import cn from 'classnames';
import DataTable from '@containers/DataTable';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import Reason from '../components/Reason';
import { Column, Row } from '@components/Grid';
import { createSumFromObject, formatDate } from '@utils/formatValues';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { getCurrentLanguage } from '@utils/lang';
import {
  getProblematicOnWayParcels,
  TProblematicOnWayParcel,
  TReexportTariffParams
} from '@api/apiClient/problematic';
import { IOnWayTabProps } from '../types';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { Tooltip } from '@components/new/Tooltip';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const OnWayTab: React.FC<IOnWayTabProps> = (props: IOnWayTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    reexportTariffsData,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);
  const [parcelId, setParcelId] = useState<string>('');

  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const createTariffsTooltip = (reexportTariffs: TReexportTariffParams[]): string => (
      reexportTariffs[0].marketplaceType + ' ' + t(`countries.${reexportTariffs[0].countryCode}`) + ` — ${createSumFromObject(reexportTariffs[0].price)}` + '\n' +
      reexportTariffs[1].marketplaceType + ' ' + t(`countries.${reexportTariffs[1].countryCode}`) + ` — ${createSumFromObject(reexportTariffs[1].price)}` + '\n' +
      reexportTariffs[2].marketplaceType + ` ${t('problematicParcelsPage.and')} ` + reexportTariffs[4].marketplaceType + ` — ${createSumFromObject(reexportTariffs[2].price)}` + '\n' +
      reexportTariffs[6].marketplaceType + ` — ${createSumFromObject(reexportTariffs[6].price)}`
  );

  const columns: TableColumn<TProblematicOnWayParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcelId(row.data?.id);
          }}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.reason'),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => <Reason value={row.data.reason}/>,
      fixWidth: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.service'),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => row.data.serviceName,
      fixWidth: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.price'),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {createSumFromObject(row.data.declaredValue)}
            </Text>
            {/*<Text*/}
            {/*    color={TextColor.DARK_MUTED}*/}
            {/*    size={TextSize.XS}*/}
            {/*>*/}
            {/*  {createSumFromObject(row.data.declaredValue)}*/}
            {/*</Text>*/}
          </div>
      ),
      alignment: 'right',
      width: '120px'
    },
    {
      header: <div className={styles.title}>
        {t('problematicParcelsPage.sum')}
        {reexportTariffsData && (
            <Tooltip
                action={
                  <img
                      alt="Info"
                      src={InfoIcon}
                  />
                }
                position="top"
                text={createTariffsTooltip(reexportTariffsData.reexportTariffs)}
                whiteSpace="pre-wrap"
            />
        )}
      </div>,
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => (
          <div className={styles.actionPrice}>
            {createSumFromObject(row.data.actionPrice)}
          </div>
      ),
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.PRIMARY,
      width: '100px'
    },
    {
      header: t('problematicParcelsPage.seller'),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => (
          <AvatarWithMarketplace
              id={row.data.sellerId}
              name={row.data.sellerName}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('problematicParcelsPage.decisionDate'),
      accessor: (row: TableRowType<TProblematicOnWayParcel>) => formatDate(row.data.decisionWasMadeAt, 'dd.MM.yy'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    }
  ], [currentLanguage]);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('problematicParcelsPage.onWay.description')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="problematic-onWay"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackOrExternalNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'serviceIds',
                      type: FilterTypes.SERVICE_PROBLEMATIC
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'marketplaceType',
                      type: FilterTypes.MARKETPLACE
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TProblematicOnWayParcel>
            columns={columns}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getProblematicOnWayParcels}
            notFound={t('problematicParcelsPage.noParcels')}
            pending={!isInitialized}
            queryKey="getProblematicOnWayParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={onCloseParcel}
            onClose={onCloseParcel}
            width={900}
        >
          <ParcelModalWindow parcelId={parcelId} isProblematic />
        </Modal>
      </div>
  );
};

export default OnWayTab;
